import { Link } from "react-router-dom";
import {
  Award,
  Users,
  BookOpen,
  MessageSquare,
  Edit,
  Globe,
  Settings,
  Calendar,
  CheckCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function FeaturesPage() {
  const features = [
    {
      icon: <Award style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />,
      title: "Expert-Led Training",
      description:
        "Learn from certified linguists and industry professionals with years of international experience and cultural expertise.",
      benefits: [
        "Certified instructors with advanced degrees",
        "Native speakers and cultural experts",
        "Industry-specific professional experience",
        "Continuous professional development",
      ],
      colorClass: "yellow",
    },
    {
      icon: <BookOpen style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />,
      title: "Business-Focused Curriculum",
      description:
        "Structured learning programs designed specifically for workplace communication, professional growth, and international business success.",
      benefits: [
        "Real-world business scenarios and case studies",
        "Industry-specific terminology and protocols",
        "Professional communication skills development",
        "Cultural competency and business etiquette",
      ],
      colorClass: "blue",
    },
    {
      icon: <Users style={{ width: '2rem', height: '2rem', color: '#16a34a' }} />,
      title: "Live & Interactive Classes",
      description:
        "Small group sessions with real-time feedback, interactive practice, and personalized attention for optimal learning outcomes.",
      benefits: [
        "Small class sizes for personalized attention",
        "Real-time feedback and correction",
        "Interactive speaking and listening practice",
        "Peer learning and collaboration opportunities",
      ],
      colorClass: "green",
    },
    {
      icon: <MessageSquare style={{ width: '2rem', height: '2rem', color: '#9333ea' }} />,
      title: "Translation & Interpretation Support",
      description:
        "Professional translation and interpretation services to bridge language gaps in business communications and international operations.",
      benefits: [
        "Professional document translation services",
        "Real-time interpretation for meetings and events",
        "Website and marketing material localization",
        "Certified translation for official documents",
      ],
      colorClass: "purple",
    },
    {
      icon: <Edit style={{ width: '2rem', height: '2rem', color: '#4f46e5' }} />,
      title: "Editing & Localization Services",
      description:
        "Comprehensive editing, proofreading, and localization services to ensure accurate, culturally appropriate, and professional communications.",
      benefits: [
        "Professional proofreading and editing",
        "Cultural appropriateness review",
        "Brand voice and style consistency",
        "Multilingual content optimization",
      ],
      colorClass: "indigo",
    },
    {
      icon: <Calendar style={{ width: '2rem', height: '2rem', color: '#db2777' }} />,
      title: "Flexible Learning Options",
      description:
        "Customizable schedules and learning formats designed to accommodate busy professionals and diverse learning preferences.",
      benefits: [
        "Multiple time slots and scheduling options",
        "Weekend and evening class availability",
        "Online and in-person learning modes",
        "Accelerated and standard-pace programs",
      ],
      colorClass: "pink",
    },
    {
      icon: <Globe style={{ width: '2rem', height: '2rem', color: '#0d9488' }} />,
      title: "B2B Language Solutions",
      description:
        "Comprehensive corporate language solutions including team training, interpretation services, and enterprise-level communication support.",
      benefits: [
        "Corporate team training programs",
        "Executive and leadership language coaching",
        "Business event interpretation services",
        "Enterprise translation and localization",
      ],
      colorClass: "teal",
    },
    {
      icon: <Settings style={{ width: '2rem', height: '2rem', color: '#ea580c' }} />,
      title: "Cultural Training & Localization",
      description:
        "Deep cultural training and localization expertise to ensure effective cross-cultural communication and business success.",
      benefits: [
        "Cross-cultural communication training",
        "Business etiquette and protocol instruction",
        "Regional market insights and strategies",
        "Cultural sensitivity and awareness development",
      ],
      colorClass: "orange",
    },
  ];

  const learningMethods = [
    {
      title: "Interactive Video Lessons",
      description:
        "High-quality video content with interactive elements and real-time engagement",
      icon: "🎥",
    },
    {
      title: "Real-World Case Studies",
      description:
        "Actual business scenarios and case studies from international markets",
      icon: "💼",
    },
    {
      title: "Cultural Immersion Activities",
      description:
        "Practical cultural training through simulated international business environments",
      icon: "🌍",
    },
    {
      title: "One-on-One Coaching",
      description:
        "Personalized coaching sessions with expert instructors for targeted skill development",
      icon: "👤",
    },
  ];

  const certifications = [
    {
      name: "Business Language Certificate",
      description: "Official certification for business language proficiency",
      levels: "All levels available",
    },
    {
      name: "Cultural Competency Certification",
      description:
        "Recognized certification for cross-cultural business communication",
      levels: "Professional & Executive",
    },
    {
      name: "Professional Translation Certificate",
      description: "Industry-recognized certification for translation services",
      levels: "Specialized fields",
    },
  ];

  return (
    <div className="features-page">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="header-content">
          <h1><span className="first-word">Our Features</span> </h1>
          <p>
            Comprehensive language education with cutting-edge features designed
            for professional success
          </p>
        </div>
      </section>

      {/* Features Introduction */}
      <section className="intro-section">
        <div className="section-content">
          <h2>Why Choose Our Language Programs</h2>
          <p>
            Our comprehensive approach combines expert instruction, business-focused
            curriculum, and flexible learning options to deliver exceptional results
            for professionals and businesses worldwide.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="section-content">
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className={`feature-card ${feature.colorClass}`}>
                <div className="feature-header">
                  <div className="icon-wrapper">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.description}</p>
                <div className="benefits">
                  <h4>Key Benefits:</h4>
                  <ul>
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex}>
                        <div className="dot"></div>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Methods */}
      <section className="learning-methods-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Diverse Learning Methods</h2>
            <p>Multiple approaches to suit different learning styles and preferences</p>
          </div>
          <div className="methods-grid">
            {learningMethods.map((method, index) => (
              <div key={index} className="method-card">
                <div className="icon">{method.icon}</div>
                <h3>{method.title}</h3>
                <p>{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certifications-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Industry-Recognized Certifications</h2>
            <p>Earn credentials that validate your professional language skills</p>
          </div>
          <div className="certs-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="cert-card">
                <div className="cert-icon">
                  <Award style={{ width: '2rem', height: '2rem' }} />
                </div>
                <h3>{cert.name}</h3>
                <p>{cert.description}</p>
                <div className="levels">{cert.levels}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="support-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Comprehensive Support</h2>
            <p>We're here to help you succeed every step of the way</p>
          </div>
          <div className="support-grid">
            <div className="support-card">
              <div className="icon-wrapper blue">
                <Users style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
              </div>
              <h3>Student Support</h3>
              <p>Dedicated support team available to assist with any questions</p>
            </div>
            <div className="support-card">
              <div className="icon-wrapper green">
                <CheckCircle style={{ width: '2rem', height: '2rem', color: '#16a34a' }} />
              </div>
              <h3>Progress Tracking</h3>
              <p>Monitor your learning progress with detailed analytics</p>
            </div>
            <div className="support-card">
              <div className="icon-wrapper yellow">
                <Award style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />
              </div>
              <h3>Career Guidance</h3>
              <p>Professional guidance for career advancement opportunities</p>
            </div>
            <div className="support-card">
              <div className="icon-wrapper purple">
                <Globe style={{ width: '2rem', height: '2rem', color: '#9333ea' }} />
              </div>
              <h3>Global Network</h3>
              <p>Connect with professionals worldwide through our community</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}